import {FC} from 'react'
import {Link} from 'react-router-dom'
import {useAuth} from '../../../app/modules/auth'

type AppLink = {
  title: string
  to: string
  icon?: string
}

type Props = {
  title: string
  createdBy: string
  tag: string
  description: string
  icon: string
  iconColor?: string
  links: AppLink[]
  roleNeeded?: string[]
  projectNameNeeded?: string[]
  vendorNameNeeded?: string[]
  dashboardsNeeded?: string[]
}

const hasDashboardPermission = (
  required: string[],
  userDashboards: {name: string}[] = []
): boolean => {
  return required.some((req) => userDashboards.some((d) => d.name === req))
}

export const DEAppCard: FC<Props> = ({
  title,
  createdBy,
  tag,
  description,
  icon,
  iconColor,
  links,
  roleNeeded,
  projectNameNeeded,
  vendorNameNeeded,
  dashboardsNeeded,
}) => {
  const {currentUser} = useAuth()
  const userRole = currentUser?.decoded?.role
  const userProjectName = currentUser?.decoded?.projectName
  const userVendorName = currentUser?.decoded?.vendorName
  const userDashboards = currentUser?.decoded?.dashboards || []
  const userEmail = currentUser?.decoded?.email

  // Special case for lucas@designereyes.com to see all apps
  if (userEmail === 'lucas@designereyes.com') {
    // Show card regardless of permissions
  } else {
    // Check normal authorization logic
    if (
      (roleNeeded && userRole && !roleNeeded.includes(userRole)) ||
      (projectNameNeeded && (!userProjectName || !projectNameNeeded.includes(userProjectName))) ||
      (vendorNameNeeded && (!userVendorName || !vendorNameNeeded.includes(userVendorName)))
    ) {
      return null
    }

    if (dashboardsNeeded && dashboardsNeeded.length > 0) {
      if (!hasDashboardPermission(dashboardsNeeded, userDashboards)) {
        return null
      }
    }
  }

  return (
    <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12 mb-6'>
      <div className='card h-100 shadow-sm'>
        <div className='card-body d-flex flex-column p-6'>
          
          {/* Top Section: Icon + Content */}
          <div className='d-flex align-items-start mb-4'>
            {/* Icon */}
            <div 
              className='d-flex align-items-center justify-content-center me-4 flex-shrink-0' 
              style={{
                width: '60px',
                height: '60px'
              }}
            >
              <i 
                className={icon} 
                style={{
                  color: iconColor || '#6c757d',
                  fontSize: '3.5rem'
                }}
              ></i>
            </div>

            {/* Content */}
            <div className='flex-grow-1'>
              {/* Title */}
              <h4 className='fw-bold mb-1 fs-5'>{title}</h4>

              {/* Created By */}
              <p className='text-muted mb-2 fs-7'>Created by {createdBy}</p>

              {/* Tag Pill */}
              <span 
                className='badge rounded-pill px-3 py-1'
                style={{
                  backgroundColor: 'transparent',
                  color: '#009ef7',
                  border: '1px solid #009ef7',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}
              >
                {tag}
              </span>
            </div>
          </div>

          {/* Description */}
          <p className='text-muted mb-4 flex-grow-1 fs-7'>{description}</p>

          {/* Action Links */}
          <div className='d-flex flex-column gap-2 mt-auto'>
            {links.map((link, index) => (
              <Link
                key={index}
                to={link.to}
                className='btn btn-sm btn-light-primary d-flex align-items-center'
              >
                {link.icon && <i className={`${link.icon} me-2`}></i>}
                {link.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
